import {
  Button,
  Card,
  Input,
  RadixTable,
  TColumn,
} from '@features/ui';
import Pagination from '@features/ui/components/pagination.tsx';
import { formatDateByHours } from '@lib/utils.ts';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { getAbandonedCarts } from '@src/services/abandonedCart.ts';
import {
  TAbandonedCart,
  TAbandonedCartItem,
} from '@src/types/abandonedCart.ts';

const PAGE_SIZE = 20;

export const AbandonedCartPage = () => {
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchDraft, setSearchDraft] = useState('');
  const [totalItems, setTotalItems] = useState(0);
  const [totalPage, setTotalPage] = useState(1);
  const [data, setData] = useState<TAbandonedCart[]>([]);

  const fetchAbandonedCarts = useCallback(async (nextPage: number, nextSearch: string) => {
    setLoading(true);
    try {
      const trimmedSearch = nextSearch.trim();
      const res = await getAbandonedCarts({
        page: nextPage,
        page_size: PAGE_SIZE,
        search: trimmedSearch || undefined,
      });

      setData(res.data.items ?? []);
      setTotalItems(res.data.total ?? 0);
      setTotalPage(
        Math.max(1, Math.ceil((res.data.total ?? 0) / (res.data.page_size || PAGE_SIZE)))
      );
    } catch (error) {
      console.error('Failed to fetch abandoned carts:', error);
      setData([]);
      setTotalItems(0);
      setTotalPage(1);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleRefresh = useCallback(() => {
    fetchAbandonedCarts(page, search);
  }, [fetchAbandonedCarts, page, search]);

  const handleSearch = useCallback(async () => {
    const nextSearch = searchDraft.trim();

    setSearch(nextSearch);
    setPage(1);

    if (page === 1) {
      await fetchAbandonedCarts(1, nextSearch);
    }
  }, [fetchAbandonedCarts, page, searchDraft]);

  const handleChangePage = useCallback(
    async (nextPage: number) => {
      setPage(nextPage);
      await fetchAbandonedCarts(nextPage, search);
    },
    [fetchAbandonedCarts, search]
  );

  useEffect(() => {
    fetchAbandonedCarts(page, search);
  }, [fetchAbandonedCarts, page, search]);

  const getDisplayName = useCallback((item: TAbandonedCartItem) => {
    return (
      item.product?.display_name_web ||
      item.product?.display_name_app ||
      item.product?.title ||
      item.item_title ||
      '-'
    );
  }, []);

  const getAccountItems = useCallback(
    (invoiceItems: TAbandonedCartItem[] | null | undefined) => {
      return (invoiceItems || []).filter(
        (item) => item.item_type === 'product' || item.item_type === 'addon'
      );
    },
    []
  );

  const recordsInfo = useMemo(() => {
    if (totalItems === 0) {
      return '0-0';
    }

    const start = (page - 1) * PAGE_SIZE + 1;
    const end = Math.min(page * PAGE_SIZE, totalItems);

    return `${start}-${end}`;
  }, [page, totalItems]);

  const columns: TColumn<TAbandonedCart>[] = useMemo(
    () => [
      {
        title: 'NAME',
        render: (row) => {
          const name = [row?.first_name, row?.last_name].filter(Boolean).join(' ');

          return <p className="text-sm">{name || '-'}</p>;
        },
      },
      {
        title: 'EMAIL',
        render: (row) => <p className="text-sm">{row?.email || '-'}</p>,
      },
      {
        title: 'ADDRESS',
        render: (row) => (
          <p className="text-sm max-w-xs truncate" title={row?.address || undefined}>
            {row?.address || '-'}
          </p>
        ),
      },
      {
        title: 'PHONE',
        render: (row) => <p className="text-sm">{row?.phone || '-'}</p>,
      },
      {
        title: 'ACCOUNT',
        render: (row) => {
          const accountItems = getAccountItems(row?.invoice_items);

          return (
            <div className="text-sm flex flex-wrap gap-1.5">
              {accountItems.length > 0 ? (
                accountItems.map((acc, index) => (
                  <div
                    key={String(
                      acc?.id ?? `${row?.invoice_id ?? 'subscription'}-${index}`
                    )}
                    className={
                      'min-w-[133px] w-fit min-h-[25px] px-2 py-1 items-center justify-center flex text-[#00E579] bg-[#14CA744D] rounded-[5px] text-[10px] font-medium break-words'
                    }
                  >
                    {getDisplayName(acc)}
                  </div>
                ))
              ) : (
                <p>-</p>
              )}
            </div>
          );
        },
      },
      {
        title: 'LAST UPDATED',
        render: (row) => (
          <p className="text-sm">
            {row?.last_updated ? formatDateByHours(row.last_updated) : '-'}
          </p>
        ),
      },
    ],
    [getAccountItems, getDisplayName]
  );

  return (
    <div>
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold mb-2">
            Abandoned Cart
            {loading && (
              <span className="ml-3 text-sm text-gray-400 font-normal">
                Loading...
              </span>
            )}
          </h1>
          <p className="text-sm text-gray-400">
            Users who didn&lsquo;t complete their checkout. Showing records {recordsInfo} from {totalItems.toLocaleString()}
          </p>
        </div>
        <Button
          className="gap-2"
          disabled={loading}
          size="sm"
          variant="default"
          onClick={handleRefresh}
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <Card className="p-6">
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center">
          <Input
            className="max-w-lg"
            placeholder="Search by email, first name, or last name"
            textSize="sm"
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
          />
          <Button
            disabled={loading}
            size="sm"
            variant="secondary"
            onClick={handleSearch}
          >
            Search
          </Button>
        </div>

        <RadixTable columns={columns} data={data} />

        {totalPage > 1 && (
          <div className="mt-6 flex justify-center">
            <Pagination current={page} total={totalPage} onChange={handleChangePage} />
          </div>
        )}

        {data.length === 0 && !loading && (
          <div className="text-center py-8 text-gray-400">
            No abandoned carts found
          </div>
        )}
      </Card>
    </div>
  );
};
